'use client'

import { useEffect, type ReactNode } from 'react'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { routes } from '@/config/routes'
import { useMyInvestorCases } from '@/features/investors'

/**
 * The investor cannot see their dashboard until an advisor publishes at least one of their
 * cases — this is the product's core "prescription, not self-serve" promise. Until then they
 * see `/review-status`. Matches `RequirePublishedCase` in the design source: any one published
 * case (not necessarily every owned flow) unlocks the whole dashboard shell; each service tab's
 * own per-flow pending/empty state handles the rest.
 *
 * Deliberately not applied to `/profile` — see PROGRESS.md for why that route stays reachable
 * regardless of case status (its real, working password-change flow shouldn't be gated behind
 * an unrelated business condition).
 */
export function RequireDashboardAccess({ children }: { children: ReactNode }) {
  const router = useRouter()
  const { data: investorCases = [], isLoading } = useMyInvestorCases()
  const hasPublishedCase = investorCases.some((c) => c.caseStatus === 'published')

  useEffect(() => {
    if (!isLoading && !hasPublishedCase) router.replace(routes.reviewStatus() as Route)
  }, [isLoading, hasPublishedCase, router])

  if (isLoading || !hasPublishedCase) return null

  return children
}
